import {
  HeadContent,
  Scripts,
  createRootRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router"
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools"
import { TanStackDevtools } from "@tanstack/react-devtools"

import appCss from "../styles.css?url"
import { queryClient } from "@/lib/query-client.ts"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister"
import type { Persister } from "@tanstack/query-persist-client-core"
import { PageTitleProvider } from "@/contexts/PageTitleContext.tsx"
import { initCustomerIO } from "@/lib/customer-io.ts"
import { useEffect } from "react"
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@/components/ui/item.tsx"
import { FaceNeutral, Home } from "lucide-react"
import { Button } from "@/components/ui/button.tsx"
import { LogoFull } from "@/components/LogoFull"
import { Toaster } from "@/components/ui/toast.tsx"
import { getStoredLocale } from "@/lib/utils.ts"
import { useTranslation } from "react-i18next"
import { initGTM, trackPageView } from "@/lib/gtm.ts"

export const Route = createRootRoute({
  ssr: false,
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: "Egab",
      },
    ],
    links: [
      { rel: "preload", href: appCss, as: "style" },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  notFoundComponent: () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    return (
      <div className="mx-auto flex h-dvh w-lvw flex-col bg-sidebar p-4 pt-16">
        <div className="mb-8 flex flex-col items-center">
          <LogoFull />
        </div>
        <h1 className="text-center text-5xl font-bold text-secondary-foreground">
          404
        </h1>
        <Item
          variant="muted"
          className="flex flex-col items-center justify-center gap-3"
        >
          <ItemContent className="mt-8 flex items-center gap-4 text-center">
            <FaceNeutral size={48} className="text-secondary" />
            <ItemTitle className="font-heading text-xl">
              Sorry! Something went wrong!
            </ItemTitle>
            <ItemDescription>
              Please refresh the page or go Home.
            </ItemDescription>
          </ItemContent>
          <ItemActions className="m-8">
            <Button variant="outline" onClick={() => navigate({ to: "/" })}>
              <Home /> <span className="not-md:hidden">Go Home</span>
            </Button>
          </ItemActions>
        </Item>
      </div>
    )
  },
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  const persister =
    typeof window !== "undefined"
      ? createAsyncStoragePersister({
          storage: window.localStorage,
        })
      : undefined

  const location = useLocation()
  let locale
  useEffect(() => {
    locale = getStoredLocale()
    initCustomerIO()
    initGTM()
  }, [])

  useEffect(() => {
    trackPageView(location.pathname)
  }, [location.pathname])

  useEffect(() => {
    const handlePreloadError = () => {
      window.location.reload()
    }

    window.addEventListener("vite:preloadError", handlePreloadError)

    return () => {
      window.removeEventListener("vite:preloadError", handlePreloadError)
    }
  }, [])

  return (
    <html
      lang={locale}
      dir={locale === "ar" ? "rtl" : "ltr"}
      className={locale === "ar" ? "font-arabic" : ""}
    >
      <head>
        <HeadContent />
      </head>
      <body className={locale === "ar" ? "font-arabic" : ""}>
        <PageTitleProvider defaultTitle="">
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister: persister as Persister,
              maxAge: 24 * 60 * 60 * 1000,
            }}
          >
            {children}
            <Toaster timeout={3000} />
            <TanStackDevtools
              config={{
                position: "bottom-right",
              }}
              plugins={[
                {
                  name: "Tanstack Router",
                  render: <TanStackRouterDevtoolsPanel />,
                },
              ]}
            />
            <Scripts />
          </PersistQueryClientProvider>
        </PageTitleProvider>
      </body>
    </html>
  )
}
